import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, AlertTriangle } from "lucide-react";
import { PRIORITY_COUNTRIES, HIDDEN_COUNTRIES } from "@/lib/countries";
import Flag from "@/components/Flag";
import ContactPicker from "@/components/ContactPicker";

export default function NewMission() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledClientId = searchParams.get("client_id") || "";
  const { user } = useAuth();
  // Iter-208 — FORFAIT creation requires the finance permission. Used to hide
  // the option from creators that cannot manage the total HT / milestones.
  const canViewFinance = user.role === "CEO" || (user.permissions || []).includes("view_mission_financials");
  const [form, setForm] = useState({
    title: "", description: "", client_id: prefilledClientId, type: "REGIE",
    currency: "MAD", budget_jh: "", start_date: "", end_date: "", country: "MA",
    billing_delay_months: 1,
    billing_delay_days: "",
    manager_id: "",
    contact_name: "", contact_email: "", contact_gender: "",
    final_client_name: "",
    // Iter-183 — forfait
    total_amount_ht: "",
    // Iter-191 — forfait: "BC seul" (no master contract required for billing).
    bdc_only: false,
    // Iter-203 — Weekly CRA opt-in (default OFF; explicit client request needed).
    weekly_cra_enabled: false,
  });
  // Iter-183 — forfait milestones (dynamic list).
  const [milestones, setMilestones] = useState([
    { name: "", percentage: "", planned_date: "" },
  ]);
  // Iter-40: track whether the user manually overrode the currency. When false we keep
  // syncing currency to the country (MA → MAD, anything else → EUR).
  const [currencyTouched, setCurrencyTouched] = useState(false);
  const [clients, setClients] = useState([]);
  const [countries, setCountries] = useState([]);
  const [managers, setManagers] = useState([]);
  const [clientDialog, setClientDialog] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", group_name: "", address: "", ice: "" });
  // Iter-85: country picker collapsed by default; "Autre…" expands to the full ISO list.
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);

  // Iter-39: only CEO + GESTIONNAIRE see the manager picker. Manager creators stay on the
  // legacy default (themselves). For CEO we add a "(Myself)" entry on top of the list.
  const showManagerPicker = user?.role === "CEO" || user?.role === "GESTIONNAIRE";
  const includeSelfOption = user?.role === "CEO";

  // Iter-187 — draft persistence + navigation guard.
  // The form is "dirty" as soon as any field deviates from the initial empty state.
  // On dirty: we attach both a `beforeunload` listener (covers tab close / hard reload)
  // and a document-level click-capture listener that intercepts `<a href="/...">` clicks
  // (covers in-app navigation since this codebase uses BrowserRouter without data routers).
  // The dialog offers 3 outcomes: discard, save-as-draft (localStorage), or stay.
  const DRAFT_KEY = "baim:new_mission_draft:v1";
  const [leaveDialog, setLeaveDialog] = useState({ open: false, pendingHref: null });
  const [draftRestoreOffered, setDraftRestoreOffered] = useState(false);
  const [savedDraft, setSavedDraft] = useState(null);

  // Restore offer on mount.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && (parsed.form || parsed.milestones)) {
          setSavedDraft(parsed);
          setDraftRestoreOffered(true);
        }
      }
    } catch { /* ignore parse errors */ }
  }, []);

  const isFormDirty = () => {
    if (form.title || form.description) return true;
    if (form.client_id && form.client_id !== prefilledClientId) return true;
    if (form.budget_jh || form.start_date || form.end_date) return true;
    if (form.manager_id || form.contact_name || form.contact_email) return true;
    if (form.final_client_name || form.total_amount_ht) return true;
    if (form.type !== "REGIE") return true;
    if (milestones.length > 1) return true;
    if (milestones[0] && (milestones[0].name || milestones[0].percentage || milestones[0].planned_date)) return true;
    return false;
  };

  // beforeunload: native browser confirm on tab close / refresh.
  useEffect(() => {
    const handler = (e) => {
      if (!isFormDirty()) return;
      e.preventDefault();
      e.returnValue = ""; // Chrome requires this.
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, milestones]);

  // In-app navigation intercept (capture click on internal anchors).
  useEffect(() => {
    const handler = (e) => {
      if (!isFormDirty()) return;
      // Find the closest anchor.
      const a = e.target.closest && e.target.closest("a");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      // Only intercept internal SPA navigation (starts with `/` and not `/missions/new`).
      if (!href.startsWith("/") || href.startsWith("//")) return;
      if (href === "/missions/new" || href.startsWith("/missions/new?")) return;
      // Ignore modifier-key clicks (new tab / window).
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      e.preventDefault();
      e.stopPropagation();
      setLeaveDialog({ open: true, pendingHref: href });
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, milestones]);

  const saveDraftAndLeave = () => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        form, milestones, saved_at: new Date().toISOString(),
      }));
      toast.success("Brouillon enregistré — restaurable au prochain accès.");
    } catch {
      toast.error("Impossible d'enregistrer le brouillon (stockage indisponible).");
    }
    const target = leaveDialog.pendingHref;
    setLeaveDialog({ open: false, pendingHref: null });
    if (target) navigate(target);
  };

  const discardAndLeave = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
    const target = leaveDialog.pendingHref;
    setLeaveDialog({ open: false, pendingHref: null });
    if (target) navigate(target);
  };

  const restoreDraft = () => {
    if (!savedDraft) return;
    if (savedDraft.form) setForm(savedDraft.form);
    if (savedDraft.milestones && savedDraft.milestones.length > 0) setMilestones(savedDraft.milestones);
    setDraftRestoreOffered(false);
    toast.success("Brouillon restauré.");
  };

  const dismissDraftOffer = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
    setDraftRestoreOffered(false);
  };

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/holidays/countries").then((r) => setCountries(r.data)).catch(() => setCountries([]));
    if (showManagerPicker) {
      api.get("/users/assignable-managers")
        .then((r) => {
          // For a CEO: drop themselves from the list (they appear under the dedicated "Myself"
          // option). For a GESTIONNAIRE: drop themselves too — they cannot manage missions.
          const filtered = r.data.filter((u) => u.user_id !== user?.user_id);
          // Keep only MANAGER role for gestionnaire (they shouldn't pick a CEO/ADMIN by mistake).
          // Actually backend allows CEO/ADMIN too for the rare case of CEO-managed missions; we
          // expose all valid assignees to keep things simple.
          setManagers(filtered);
        })
        .catch(() => setManagers([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showManagerPicker]);

  const createClient = async () => {
    if (!newClient.name) return toast.error("Client name required");
    try {
      const r = await api.post("/clients", newClient);
      setClients([...clients, r.data]);
      setForm({ ...form, client_id: r.data.client_id });
      setClientDialog(false);
      setNewClient({ name: "", group_name: "", address: "", ice: "" });
      toast.success("Client created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  // Iter-183 — milestone helpers + computed sum.
  const addMilestone = () => setMilestones([...milestones, { name: "", percentage: "", planned_date: "" }]);
  const removeMilestone = (idx) => setMilestones(milestones.filter((_, i) => i !== idx));
  const updateMilestone = (idx, key, value) => {
    const copy = [...milestones];
    copy[idx] = { ...copy[idx], [key]: value };
    setMilestones(copy);
  };
  const milestonesSum = milestones.reduce((s, m) => s + (parseFloat(m.percentage) || 0), 0);
  const milestonesRemaining = Math.round((100 - milestonesSum) * 100) / 100;
  const milestonesValid = Math.abs(milestonesSum - 100) < 0.01;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) return toast.error("Select a client");
    if (showManagerPicker && !form.manager_id) {
      return toast.error("Select a manager for this mission");
    }
    // Iter-183 — forfait extra validation.
    if (form.type === "FORFAIT") {
      const total = parseFloat(form.total_amount_ht);
      if (!total || total <= 0) return toast.error("Montant total HT obligatoire pour Forfait");
      if (milestones.length === 0) return toast.error("Ajoute au moins un palier");
      if (!milestonesValid) return toast.error(`La somme des paliers doit être 100% (actuel : ${milestonesSum.toFixed(2)}%)`);
      for (const m of milestones) {
        if (!(m.name || "").trim()) return toast.error("Chaque palier doit avoir un nom");
        const p = parseFloat(m.percentage);
        if (!p || p <= 0 || p > 100) return toast.error("Chaque palier doit avoir un pourcentage > 0 et ≤ 100");
      }
    }
    try {
      // The "self" option for CEO is encoded as the literal "__SELF__" — translate it back
      // to the current user's id before sending to the backend.
      const managerToSend = form.manager_id === "__SELF__" ? user.user_id : form.manager_id;
      const payload = {
        ...form,
        manager_id: managerToSend || undefined,
        budget_jh: form.type === "FORFAIT" ? 0 : parseFloat(form.budget_jh),
      };
      if (form.type === "FORFAIT") {
        payload.total_amount_ht = parseFloat(form.total_amount_ht);
        payload.milestones = milestones.map((m) => ({
          name: m.name.trim(),
          percentage: parseFloat(m.percentage),
          planned_date: m.planned_date || null,
        }));
        payload.bdc_only = !!form.bdc_only;
      } else {
        delete payload.total_amount_ht;
        delete payload.bdc_only;
      }
      // Iter-194w — convert empty billing_delay_days to null; validate range.
      if (payload.billing_delay_days === "" || payload.billing_delay_days == null) {
        payload.billing_delay_days = null;
      } else {
        const n = parseInt(payload.billing_delay_days, 10);
        if (!Number.isFinite(n) || n < 1 || n > 365) {
          toast.error("Override en jours : 1 à 365 (ou vide).");
          return;
        }
        payload.billing_delay_days = n;
      }
      // Iter-203 — explicit boolean (default false). The backend defaults to false too.
      payload.weekly_cra_enabled = !!form.weekly_cra_enabled;
      const r = await api.post("/missions", payload);
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      toast.success("Mission created — now add roles and staffing");
      navigate(`/missions/${r.data.mission_id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const F = (k, props = {}) => (
    <Input
      data-testid={`mission-field-${k}`}
      value={form[k]}
      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
      {...props}
    />
  );

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-3xl">
      <div className="mb-10">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">New</div>
        <h1 className="font-heading text-2xl md:text-4xl font-bold tracking-tight">Create mission</h1>
        <p className="text-zinc-500 mt-3 max-w-xl">Define the scope and envelope. Add roles and staff collaborators after creation.</p>
      </div>

      {draftRestoreOffered && savedDraft && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-3" data-testid="new-mission-draft-banner">
          <div className="flex-1 min-w-0 text-sm text-amber-900">
            <div className="font-semibold">Brouillon disponible</div>
            <div className="text-xs text-amber-800 mt-0.5">
              Une mission non enregistrée a été retrouvée
              {savedDraft.saved_at ? ` (${new Date(savedDraft.saved_at).toLocaleString("fr-FR")})` : ""}.
              {savedDraft.form?.title ? ` — "${savedDraft.form.title}".` : ""}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={restoreDraft}
              data-testid="new-mission-draft-restore"
              className="px-3 h-8 bg-brand-navy text-white text-xs rounded-md hover:bg-brand-navy-600"
            >Restaurer</button>
            <button
              type="button"
              onClick={dismissDraftOffer}
              data-testid="new-mission-draft-dismiss"
              className="px-3 h-8 bg-white border border-zinc-300 text-xs rounded-md hover:bg-zinc-50"
            >Ignorer</button>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-lg p-8 space-y-5">
        <div>
          <Label htmlFor="title">Title</Label>
          {F("title", { id: "title", placeholder: "Digital Transformation — Phase 2", required: true })}
        </div>
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            data-testid="mission-field-description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Scope, stakeholders, success criteria…"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Client</Label>
            <div className="flex gap-2 items-stretch min-w-0">
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="client-select" className="flex-1 min-w-0">
                  <SelectValue placeholder="Pick a client" className="truncate" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.client_id} value={c.client_id} data-testid={`client-opt-${c.client_id}`}>
                      {c.name}{c.group_name ? ` · ${c.group_name}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Dialog open={clientDialog} onOpenChange={setClientDialog}>
                <DialogTrigger asChild>
                  <button type="button" data-testid="new-client-btn" className="shrink-0 h-10 px-3 border border-zinc-200 rounded-md hover:bg-zinc-50" title="Create client">
                    <Plus size={16} />
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Create client</DialogTitle></DialogHeader>
                  <div className="space-y-4 py-2">
                    <div><Label>Name</Label><Input data-testid="new-client-name" value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} /></div>
                    <div><Label>Group / Holding (optional)</Label><Input data-testid="new-client-group" value={newClient.group_name} onChange={(e) => setNewClient({ ...newClient, group_name: e.target.value })} /></div>
                    <div><Label>Address (for invoices)</Label><Textarea data-testid="new-client-address" value={newClient.address} onChange={(e) => setNewClient({ ...newClient, address: e.target.value })} placeholder="Street, city, country" /></div>
                    <div><Label>ICE (Moroccan tax ID)</Label><Input data-testid="new-client-ice" value={newClient.ice} onChange={(e) => setNewClient({ ...newClient, ice: e.target.value })} placeholder="Optional" /></div>
                  </div>
                  <DialogFooter>
                    <button type="button" onClick={() => setClientDialog(false)} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Cancel</button>
                    <button type="button" data-testid="new-client-save" onClick={createClient} className="px-4 h-10 bg-zinc-950 text-white text-sm rounded-md">Create</button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Iter-76: when the picked client is a subcontractor, capture the END client name
              that will appear on the CRA-export PDF (CLIENT cell + signature box). */}
          {(() => {
            const c = clients.find((cl) => cl.client_id === form.client_id);
            if (!c?.is_subcontractor) return null;
            return (
              <div data-testid="mission-final-client-wrap" className="sm:col-span-2">
                <Label htmlFor="final_client_name">Final client name <span className="text-xs text-zinc-500">(for CRA export PDF)</span></Label>
                <Input
                  id="final_client_name"
                  data-testid="mission-final-client-name"
                  value={form.final_client_name}
                  onChange={(e) => setForm({ ...form, final_client_name: e.target.value })}
                  placeholder="End-customer name shown on the exported CRA (e.g. Société X)"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  <strong>{c.name}</strong> is flagged as a subcontractor. The CRA PDF will display this name in the <strong>CLIENT</strong> cell and the right-hand signature box. The email itself still ships to the subcontractor's contact.
                </p>
              </div>
            );
          })()}

          <div>
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, ...(v === "FORFAIT" ? { weekly_cra_enabled: false } : {}) })}>
              <SelectTrigger data-testid="mission-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="REGIE">Regie</SelectItem>
                {/* Iter-208 — FORFAIT requires the finance permission. Hide
                    the option from creators that cannot manage the total HT
                    and forfait milestones (the backend also 403s on submit). */}
                {canViewFinance && <SelectItem value="FORFAIT">Forfait</SelectItem>}
              </SelectContent>
            </Select>
          </div>
        </div>

        {form.type === "FORFAIT" && (
          <div className="border border-amber-200 bg-amber-50/50 rounded-md p-5 space-y-4" data-testid="mission-forfait-block">
            <div className="flex items-baseline justify-between">
              <div>
                <h3 className="font-heading font-semibold text-base">Forfait · Total amount & milestones</h3>
                <p className="text-xs text-zinc-600 mt-0.5">Define the total contract value and split it into billing milestones (sum = 100%).</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="total_amount_ht">Total amount HT ({form.currency})<span className="text-red-600"> *</span></Label>
                <Input
                  id="total_amount_ht"
                  data-testid="mission-field-total-amount-ht"
                  type="number" min="0" step="0.01"
                  value={form.total_amount_ht}
                  onChange={(e) => setForm({ ...form, total_amount_ht: e.target.value })}
                  required={form.type === "FORFAIT"}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-[0.18em] font-mono text-zinc-600">Paliers de facturation</div>
                <div className="text-xs font-mono" data-testid="mission-milestones-sum">
                  <span className={milestonesValid ? "text-emerald-700" : "text-amber-700"}>
                    Total : {milestonesSum.toFixed(2)}%
                  </span>
                  {!milestonesValid && (
                    <span className="text-zinc-500"> · reste {milestonesRemaining}%</span>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                {milestones.map((m, idx) => {
                  const amount = (parseFloat(form.total_amount_ht) || 0) * (parseFloat(m.percentage) || 0) / 100;
                  return (
                    <div key={idx} className="bg-white border border-zinc-200 rounded-md p-3" data-testid={`mission-milestone-row-${idx}`}>
                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                        <div className="sm:col-span-5">
                          <Label className="text-[10px]">Name <span className="text-red-600">*</span></Label>
                          <Input
                            data-testid={`mission-milestone-name-${idx}`}
                            value={m.name}
                            onChange={(e) => updateMilestone(idx, "name", e.target.value)}
                            placeholder="Cadrage, Specs, Recette…"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-[10px]">% <span className="text-red-600">*</span></Label>
                          <Input
                            data-testid={`mission-milestone-pct-${idx}`}
                            type="number" min="0" max="100" step="0.01"
                            value={m.percentage}
                            onChange={(e) => updateMilestone(idx, "percentage", e.target.value)}
                          />
                        </div>
                        <div className="sm:col-span-3">
                          <Label className="text-[10px]">Planned date</Label>
                          <Input
                            data-testid={`mission-milestone-date-${idx}`}
                            type="date"
                            value={m.planned_date || ""}
                            onChange={(e) => updateMilestone(idx, "planned_date", e.target.value)}
                          />
                        </div>
                        <div className="sm:col-span-2 flex items-end gap-1">
                          <div className="flex-1 text-[10px] text-zinc-500 font-mono leading-tight pb-2">
                            ≈ {amount.toFixed(0)} {form.currency}
                          </div>
                          {milestones.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeMilestone(idx)}
                              data-testid={`mission-milestone-remove-${idx}`}
                              className="h-9 w-9 border border-zinc-300 rounded-md text-zinc-500 hover:bg-red-50 hover:text-red-700 text-lg leading-none"
                              title="Remove milestone"
                            >×</button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={addMilestone}
                data-testid="mission-milestone-add"
                className="inline-flex items-center gap-1.5 px-3 h-8 bg-white border border-zinc-300 text-xs rounded-md hover:bg-zinc-50"
              >
                <Plus size={12} /> Add milestone
              </button>
              {!milestonesValid && (
                <div className="text-[11px] text-amber-800 bg-amber-100 border border-amber-200 rounded-md px-2 py-1.5" data-testid="mission-milestones-warning">
                  La somme des paliers doit être exactement 100% pour pouvoir créer la mission.
                </div>
              )}
            </div>

            {/* Iter-191 — BDC-only checkbox: makes the client master contract optional. */}
            <label className="flex items-start gap-2 text-sm pt-2 border-t border-amber-200" data-testid="mission-bdc-only-wrap">
              <input
                type="checkbox"
                checked={!!form.bdc_only}
                onChange={(e) => setForm({ ...form, bdc_only: e.target.checked })}
                className="mt-0.5"
                data-testid="mission-bdc-only-checkbox"
              />
              <span className="leading-snug">
                <strong>Bon de commande uniquement</strong>
                <span className="text-zinc-600 text-xs block">
                  The master client contract (CONTRAT_CADRE) will not be required to bill this mission — only the PO matters.
                </span>
              </span>
            </label>
          </div>
        )}

        <div className={`grid grid-cols-1 sm:grid-cols-2 ${form.type === "FORFAIT" ? "md:grid-cols-2" : "md:grid-cols-3"} gap-4`}>
          <div>
            <Label>Currency</Label>
            <Select value={form.currency} onValueChange={(v) => { setForm({ ...form, currency: v }); setCurrencyTouched(true); }}>
              <SelectTrigger data-testid="mission-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="MAD">MAD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.type !== "FORFAIT" && (
            <div data-testid="mission-budget-jh-block">
              <Label htmlFor="budget_jh">Budget (JH)</Label>
              {F("budget_jh", { id: "budget_jh", type: "number", min: "0", step: "0.5", required: true })}
            </div>
          )}
          <div>
            <Label htmlFor="start_date">Start</Label>
            {F("start_date", { id: "start_date", type: "date", required: true })}
          </div>
        </div>

        <div>
          <Label htmlFor="end_date">End (estimated)</Label>
          {F("end_date", { id: "end_date", type: "date", required: true })}
        </div>

        {/* Iter-194j — Billing delay (months). Drives the invoice due_date
            and the payment-reminder cron milestones. */}
        <div>
          <Label htmlFor="billing_delay_months">Délai de facturation (paiement client)</Label>
          <Select
            value={String(form.billing_delay_months || 1)}
            onValueChange={(v) => setForm({ ...form, billing_delay_months: parseInt(v, 10) })}
          >
            <SelectTrigger id="billing_delay_months" data-testid="mission-billing-delay">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 mois (relances J+20 / J+25 / J+29)</SelectItem>
              <SelectItem value="2">2 mois (relances J+50 / J+55 / J+59)</SelectItem>
              <SelectItem value="3">3 mois (relances J+80 / J+85 / J+89)</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-[10px] text-zinc-500 mt-1">
            Définit la date limite d'encaissement de chaque facture issue de cette mission
            et déclenche les emails de relance automatiques (gestionnaire + CEO).
          </div>
          {/* Iter-194w — optional days override (1..365) takes precedence on this mission. */}
          <div className="mt-2">
            <Label htmlFor="billing_delay_days" className="text-xs">Override en jours (optionnel)</Label>
            <input
              type="number"
              id="billing_delay_days"
              min={1}
              max={365}
              value={form.billing_delay_days ?? ""}
              onChange={(e) => setForm({ ...form, billing_delay_days: e.target.value })}
              data-testid="mission-billing-delay-days"
              placeholder="Vide = utilise le délai en mois ci-dessus"
              className="mt-1 w-full max-w-xs h-9 rounded border border-zinc-200 px-2 text-sm"
            />
            <div className="text-[10px] text-zinc-500 mt-1">
              Si renseigné, prime sur le délai en mois (ex. 45 jours, 75 jours).
              Pour des délais 30/60/90, laissez ce champ vide et utilisez le sélecteur ci-dessus.
            </div>
          </div>
        </div>

        {/* Iter-203 — Weekly CRA opt-in per mission. Iter-204 — REGIE-only. */}
        <div className="md:col-span-2">
          {(() => {
            const isForfait = form.type === "FORFAIT";
            return (
              <div className={`rounded-lg border p-4 ${isForfait ? "border-zinc-200 bg-zinc-50 opacity-70" : (form.weekly_cra_enabled ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40")}`} data-testid="mission-weekly-cra-block">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <Label htmlFor="weekly_cra_enabled" className="text-sm font-semibold flex items-center gap-2">
                      {!isForfait && !form.weekly_cra_enabled && <AlertTriangle size={14} className="text-amber-600" />}
                      CRA hebdomadaire (Weekly CRA)
                    </Label>
                    <p className="text-xs text-zinc-600 mt-1 leading-relaxed">
                      Activez ce flux uniquement <strong>si le client demande explicitement</strong> un rapport
                      d'activité hebdomadaire. Une fois activé : les collaborateurs assignés pourront générer,
                      envoyer et recevoir des relances de CRA hebdo pour cette mission.
                    </p>
                    {isForfait ? (
                      <p className="text-[11px] text-zinc-600 mt-2 italic" data-testid="mission-weekly-cra-forfait-note">
                        ⓘ Non disponible pour les missions Forfait — le CRA hebdomadaire n'a de sens qu'en facturation au temps passé (Régie).
                      </p>
                    ) : !form.weekly_cra_enabled && (
                      <p className="text-[11px] text-amber-700 mt-2 italic">
                        ⚠ Désactivé : aucun bouton "Send Weekly CRA" ne s'affichera sur le timesheet pour cette mission,
                        et la relance automatique du vendredi 14h ignorera les heures vides liées à cette mission.
                      </p>
                    )}
                  </div>
                  <Switch
                    id="weekly_cra_enabled"
                    checked={!isForfait && !!form.weekly_cra_enabled}
                    disabled={isForfait}
                    onCheckedChange={(v) => setForm({ ...form, weekly_cra_enabled: !!v })}
                    data-testid="mission-weekly-cra-toggle"
                    title={isForfait ? "Disponible uniquement pour les missions en Régie" : ""}
                  />
                </div>
              </div>
            );
          })()}
        </div>

        <div>
          <Label>Country d'intervention</Label>
          <Select
            value={form.country}
            open={countryOpen}
            onOpenChange={(o) => { setCountryOpen(o); if (!o) setShowAllCountries(false); }}
            onValueChange={(v) => {
              // Iter-40: auto-default the currency to MAD when the user picks Morocco, EUR otherwise,
              // unless they have manually picked a currency.
              setForm((prev) => ({
                ...prev,
                country: v,
                currency: currencyTouched ? prev.currency : (v === "MA" ? "MAD" : "EUR"),
              }));
            }}
          >
            <SelectTrigger data-testid="mission-country"><SelectValue placeholder="Select a country" /></SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {(() => {
                const byCode = Object.fromEntries(countries.map((c) => [c.code, c]));
                const priority = PRIORITY_COUNTRIES.map((code) => byCode[code]).filter(Boolean);
                const rest = countries
                  .filter((c) => !PRIORITY_COUNTRIES.includes(c.code) && !HIDDEN_COUNTRIES.has(c.code))
                  .sort((a, b) => a.name.localeCompare(b.name));
                return (
                  <>
                    {priority.map((c) => (
                      <SelectItem key={c.code} value={c.code} data-testid={`country-opt-${c.code}`}>
                        <span className="inline-flex items-center gap-1.5"><Flag code={c.code} size={14} alt={false} /> {c.name} ({c.code})</span>
                      </SelectItem>
                    ))}
                    {!showAllCountries && (
                      <button
                        type="button"
                        data-testid="mission-country-show-more"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowAllCountries(true); }}
                        onPointerDown={(e) => e.stopPropagation()}
                        className="w-full text-left py-1.5 pl-8 pr-2 text-sm text-zinc-500 font-medium hover:bg-zinc-100 rounded-sm focus:outline-none"
                      >
                        Autre…
                      </button>
                    )}
                    {showAllCountries && rest.map((c) => (
                      <SelectItem key={c.code} value={c.code} data-testid={`country-opt-${c.code}`}>
                        <span className="inline-flex items-center gap-1.5"><Flag code={c.code} size={14} alt={false} /> {c.name} ({c.code})</span>
                      </SelectItem>
                    ))}
                  </>
                );
              })()}
            </SelectContent>
          </Select>
          <p className="text-xs text-zinc-500 mt-1">Assigned collaborators will see this country's public holidays grayed out on their timesheet.</p>
        </div>

        {showManagerPicker && (
          <div data-testid="mission-manager-block">
            <Label>Mission manager <span className="text-red-600">*</span></Label>
            <Select value={form.manager_id} onValueChange={(v) => setForm({ ...form, manager_id: v })}>
              <SelectTrigger data-testid="mission-manager-select">
                <SelectValue placeholder="Select a manager" />
              </SelectTrigger>
              <SelectContent>
                {includeSelfOption && (
                  <SelectItem value="__SELF__" data-testid="mission-manager-opt-self">
                    (Myself)
                  </SelectItem>
                )}
                {managers.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id} data-testid={`mission-manager-opt-${u.user_id}`}>
                    {u.name}
                  </SelectItem>
                ))}
                {managers.length === 0 && !includeSelfOption && (
                  <div className="px-3 py-2 text-xs text-zinc-500">No manager available</div>
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-zinc-500 mt-1">
              {user?.role === "CEO"
                ? "You can manage the mission yourself or delegate it to a manager."
                : "This manager will own the mission (CRA validations, unlock requests, billing)."}
            </p>
          </div>
        )}

        <div className="border-t border-zinc-200 pt-4 mt-2" data-testid="mission-contact-block">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-mono mb-2">Client-side contact (for invoice email)</div>
          <ContactPicker clientId={form.client_id} value={form} autoApplyPrimary testidPrefix="mission-contact"
            onApply={(c) => setForm((f) => ({ ...f, contact_name: c.contact_name || "", contact_email: c.contact_email || "", contact_gender: c.contact_gender || "" }))}>
          <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr_1fr] gap-3">
            <div>
              <Label>Civility</Label>
              {/* Iter-198 — civilité injectée dans tous les templates emails clients
                  via {{contact_gender}}. Valeurs canoniques: "M." / "Mme." */}
              <select
                data-testid="mission-field-contact-gender"
                value={form.contact_gender}
                onChange={(e) => {
                  const g = e.target.value;
                  setForm({ ...form, contact_gender: g, ...(g === "M./Mme" ? { contact_name: "" } : {}) });
                }}
                className="w-full h-10 border border-zinc-200 rounded-md px-2 text-sm bg-white"
              >
                <option value="">—</option>
                <option value="M.">M.</option>
                <option value="Mme.">Mme.</option>
                <option value="M./Mme">M./Mme</option>
              </select>
            </div>
            <div>
              <Label>Contact name</Label>
              <Input
                data-testid="mission-field-contact-name"
                placeholder={form.contact_gender === "M./Mme" ? "Unknown contact — not used in emails" : "Jean Dupont"}
                value={form.contact_name}
                disabled={form.contact_gender === "M./Mme"}
                className={form.contact_gender === "M./Mme" ? "bg-zinc-100 text-zinc-400" : ""}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Contact email</Label>
              <Input
                data-testid="mission-field-contact-email"
                type="email"
                placeholder="contact@client.com"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              />
            </div>
          </div>
          </ContactPicker>
          <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
            Civility + name + email are <strong>required</strong> for sending invoices, reminders and CRA exports.
            The invoice PDF will be emailed to this contact with the CEO and the mission manager in Cc.
          </p>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-zinc-100">
          <button type="button" onClick={() => navigate("/missions")} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Cancel</button>
          <button type="submit" data-testid="create-mission-submit" className="px-5 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800">Create mission</button>
        </div>
      </form>

      <Dialog open={leaveDialog.open} onOpenChange={(o) => { if (!o) setLeaveDialog({ open: false, pendingHref: null }); }}>
        <DialogContent data-testid="new-mission-leave-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Quitter sans enregistrer ?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-zinc-600 leading-relaxed">
            Vous avez commencé à créer une nouvelle mission. Vos saisies seront perdues si vous quittez sans enregistrer.
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-2">
            <button
              type="button"
              onClick={discardAndLeave}
              data-testid="new-mission-leave-discard"
              className="px-3 h-9 bg-white border border-red-300 text-red-700 text-sm rounded-md hover:bg-red-50"
            >Quitter et ignorer</button>
            <button
              type="button"
              onClick={saveDraftAndLeave}
              data-testid="new-mission-leave-save"
              className="px-3 h-9 bg-brand-navy text-white text-sm rounded-md hover:bg-brand-navy-600"
            >Enregistrer en brouillon &amp; quitter</button>
            <button
              type="button"
              onClick={() => setLeaveDialog({ open: false, pendingHref: null })}
              data-testid="new-mission-leave-stay"
              className="px-3 h-9 bg-white border border-zinc-300 text-sm rounded-md hover:bg-zinc-50"
            >Rester sur la page</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
